import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  BudgetMacroItem,
  CashFund,
  CashFundsService,
  CostCenterSummary,
  Identity,
  ProjectArticle,
  ProjectSubtaskSummary,
  TransferStatusEnum,
} from '@libs/core/api';
import { FormGroup } from '@angular/forms';
import {
  buildFundCashPurchaseForm,
  FundCashPurchaseForm,
  FundCashPurchaseFormComponent,
  FundCashPurchaseFormValues,
} from '../fund-cash-purchase-form/fund-cash-purchase-form.component';
import {
  AnimationsService,
  ConfirmDialogService,
  LoadingService,
  ToastService,
} from '@libs/ui/uikit';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProjectsService } from '@front-end/core/services';
import { FundCashPurchaseTransferComponent } from '../fund-cash-purchase-transfer/fund-cash-purchase-transfer.component';
import { filter, switchMap, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'front-end-fund-cash-purchase-form-modal',
  templateUrl: './fund-cash-purchase-form-modal.component.html',
  styleUrls: ['./fund-cash-purchase-form-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundCashPurchaseFormModalComponent implements OnInit {
  @Input() project!: CostCenterSummary;
  @Input() identity!: Identity;
  @Input() cashFund?: CashFund;

  @ViewChild('purchaseForm')
  purchaseFormComponent!: FundCashPurchaseFormComponent;

  readonly TransferStatusEnum = TransferStatusEnum;
  form!: FormGroup<FundCashPurchaseForm>;
  budgetProjectTasks: BudgetMacroItem[] = [];
  allowedSubtasks: ProjectSubtaskSummary[] = [];
  allowedSuperbuyerSubtasks: ProjectSubtaskSummary[] = [];
  isDraft = false;

  isSuperBuyer$ = this.projectsService.isSuperBuyerInSelectedProject();
  cardSpendingLimits$ = this.projectsService.selectedProject$.pipe(
    switchMap((project) =>
      this.cashFundService.getCardLimits({
        projectId: project.id as number,
      })
    )
  );

  get canDeleteCashFund(): boolean {
    if (!this.cashFund) return false;

    return (
      (this.cashFund.isDraft ?? false) &&
      !this.cashFund.isPaid &&
      !this.cashFund.isAuthorized
    );
  }

  constructor(
    private modalController: ModalController,
    private cashFundService: CashFundsService,
    private toastService: ToastService,
    private projectsService: ProjectsService,
    private animationService: AnimationsService,
    private loadingService: LoadingService,
    private confirmDialogService: ConfirmDialogService
  ) {}

  ngOnInit() {
    this.form = buildFundCashPurchaseForm();

    this.budgetProjectTasks =
      this.identity.companies?.flatMap(
        (c) => c.costCenters?.flatMap((cc) => cc.macroItems ?? []) ?? []
      ) ?? [];

    this.allowedSubtasks =
      this.project.subtasks?.filter(
        (subtask) => (subtask.buyer ?? false) || (subtask.superBuyer ?? false)
      ) ?? [];

    this.allowedSuperbuyerSubtasks =
      this.project.subtasks?.filter((subtask) => subtask.superBuyer ?? false) ??
      [];

    if (this.cashFund) {
      this.form.patchValue({
        budgetItem: this.cashFund.subtask,
        notes: this.cashFund.comment,
        articleId: this.cashFund.article?.id,
        isRecurringRequested: this.cashFund.isRecurringRequested,
      });
    }
  }

  close(): void {
    this.modalController.dismiss();
  }

  submit(formValue: FundCashPurchaseFormValues): void {
    const selectedArticles: ProjectArticle[] =
      formValue.budgetItem?.projectArticles ?? [];

    if (selectedArticles.length > 0) {
      this.form.controls.articleId.setValue(selectedArticles[0].id);
    }

    this.cashFund
      ? this.cashFundService
          .editCashFund({
            id: this.cashFund?.id as number,
            body: {
              articleId: this.form.controls.articleId.value ?? undefined,
              subtaskId: this.form.controls.budgetItem.value?.id,
              comment: this.form.controls.notes.value ?? undefined,
              isRecurringRequested:
                this.form.controls.isRecurringRequested.value ?? false,
              isDraft: this.isDraft,
            },
          })
          .pipe(
            tap((response) =>
              this.modalController.dismiss(
                response,
                this.isDraft ? 'draft' : 'create'
              )
            ),
            this.loadingService.withLoading(),
            this.toastService.catchErrorWithToast(),
            untilDestroyed(this)
          )
          .subscribe()
      : this.cashFundService
          .createCashFund({
            body: {
              articleId: this.form.controls.articleId.value ?? undefined,
              subtaskId: this.form.controls.budgetItem.value?.id,
              comment: this.form.controls.notes.value ?? undefined,
              isRecurringRequested:
                this.form.controls.isRecurringRequested.value ?? false,
              isDraft: this.isDraft,
            },
          })
          .pipe(
            tap((response) =>
              this.modalController.dismiss(
                response,
                this.isDraft ? 'draft' : 'create'
              )
            ),
            this.loadingService.withLoading(),
            this.toastService.catchErrorWithToast(),
            untilDestroyed(this)
          )
          .subscribe();
  }

  setIsDraft(): void {
    this.isDraft = true;
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  get isFormInvalid(): boolean {
    return this.form.invalid;
  }

  async openTransferModal() {
    const selectedArticles: ProjectArticle[] =
      this.form.controls.budgetItem?.value?.projectArticles ?? [];

    if (selectedArticles.length > 0) {
      this.form.controls.articleId.setValue(selectedArticles[0].id);
    }
    const modal = await this.modalController.create({
      component: FundCashPurchaseTransferComponent,
      mode: 'md',
      componentProps: {
        subtaskId: this.form.controls.budgetItem.value?.id,
        cashFund: {
          budgetItem: this.form.controls.budgetItem,
          notes: this.form.controls.notes,
          articleId: this.form.controls.articleId,
        },
        cashFundId: this.cashFund?.id,
      },
      enterAnimation: this.animationService.slideLeftToRight,
      leaveAnimation: this.animationService.slideRightToLeft,
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.modalController.dismiss(null, 'transfer');
    }
  }

  get isSuperBuyerInSubtask(): boolean {
    return this.allowedSuperbuyerSubtasks.some(
      (s) => s.id === this.form.controls.budgetItem.value?.id
    );
  }

  get isCashFundTransferredToMe(): boolean {
    return (
      this.cashFund?.transferStatus === TransferStatusEnum.Transferred &&
      this.cashFund?.assignedUser?.id === this.identity.id
    );
  }

  deletePurchase(): void {
    this.confirmDialogService
      .showDialog({
        confirmButtonText: 'pages.fund-cash.purchase.deleteModal.confirm',
        cancelButtonText: 'pages.fund-cash.purchase.deleteModal.cancel',
        message: 'pages.fund-cash.purchase.deleteModal.text',
        messageArgs: {
          name: this.cashFund?.article?.description,
        },
        title: 'pages.fund-cash.purchase.deleteModal.title',
      })
      .pipe(
        filter((res): res is true => res),
        switchMap(() => {
          const request$ = this.isCashFundTransferredToMe
            ? this.cashFundService.putTransferReject({
                id: this.cashFund?.id as number,
              })
            : this.cashFundService.deleteCashFund({
                cashFundId: this.cashFund?.id as number,
              });

          return request$.pipe(
            tap(() => this.modalController.dismiss()),
            this.loadingService.withLoading(),
            this.toastService.catchErrorWithToast()
          );
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
